import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { isAxiosError } from 'axios';
import { axiosInstance } from './axiosInstance';

export const requestVideoPresign = createAsyncThunk<
    { id: string; url: string; uploadUrl: string; filename: string; contentType: string },
    { file: File }
>('project/requestVideoPresign', async ({ file }, { rejectWithValue }) => {
    try {
        const body = {
            data: {
                type: 'videos',
                attributes: {
                    filename: file.name,
                    content_type: file.type,
                },
            },
        };

        const res = await axiosInstance.post('/videos', body, {
            headers: {
                'Content-Type': 'application/vnd.api+json',
                Accept: 'application/vnd.api+json',
            },
        });

        const data = res.data;

        return {
            id: data.data.id,
            url: data.data.attributes.url,
            uploadUrl: data.meta.upload_url,
            filename: data.data.attributes.filename,
            contentType: file.type,
        };
    } catch (e: unknown) {
        const msg = isAxiosError(e)
            ? (e.response?.data?.errors ?? e.response?.data ?? e.message)
            : 'Network error';
        return rejectWithValue(msg);
    }
});

type Presign =
    | {
          method: 'POST';
          url: string;
          fields: Record<string, string>;
          fileUrl: string;
      }
    | {
          method: 'PUT';
          url: string;
          fileUrl: string;
      };

export const uploadVideoToStorage = createAsyncThunk<
    { fileUrl: string },
    { presign: Presign; file: File; onProgress?: (pct: number) => void }
>('project/uploadVideoToStorage', async ({ presign, file, onProgress }, { rejectWithValue }) => {
    try {
        if (presign.method === 'POST') {
            const fd = new FormData();
            Object.entries(presign.fields).forEach(([k, v]) => fd.append(k, v));
            fd.append('file', file);

            await axios.post(presign.url, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                maxBodyLength: Infinity,
                onUploadProgress: (evt) => {
                    if (onProgress && evt.total)
                        onProgress(Math.round((evt.loaded * 100) / evt.total));
                },
            });

            return { fileUrl: presign.fileUrl };
        } else {
            await axios.put(presign.url, file, {
                headers: { 'Content-Type': file.type || 'application/octet-stream' },
                maxBodyLength: Infinity,
                onUploadProgress: (evt) => {
                    if (onProgress && evt.total)
                        onProgress(Math.round((evt.loaded * 100) / evt.total));
                },
            });

            return { fileUrl: presign.fileUrl };
        }
    } catch (e: unknown) {
        const msg = isAxiosError(e) ? (e.response?.data ?? e.message) : 'Upload error';
        return rejectWithValue(msg);
    }
});

export const attachVideoToProject = createAsyncThunk<void, { projectId: string; videoId: string }>(
    'project/attachVideoToProject',
    async ({ projectId, videoId }, { rejectWithValue }) => {
        try {
            await axiosInstance.patch(
                `/projects/${projectId}/relationships/video`,
                {
                    data: { type: 'videos', id: videoId },
                },
                {
                    headers: {
                        'Content-Type': 'application/vnd.api+json',
                        Accept: 'application/vnd.api+json',
                    },
                },
            );
        } catch (e: unknown) {
            const msg = isAxiosError(e)
                ? (e.response?.data?.errors ?? e.response?.data ?? e.message)
                : 'Network error';
            return rejectWithValue(msg);
        }
    },
);

export const detachVideoFromProject = createAsyncThunk<void, { projectId: string }>(
    'project/detachVideoFromProject',
    async ({ projectId }, { rejectWithValue }) => {
        try {
            await axiosInstance.patch(`/projects/${projectId}/relationships/video`, {
                data: null,
            });
        } catch (e: unknown) {
            const msg = isAxiosError(e)
                ? (e.response?.data?.errors ?? e.response?.data ?? e.message)
                : 'Network error';
            return rejectWithValue(msg);
        }
    },
);
